import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { supabase } from '../services/supabase';

export type ClearstreamSyncScope = 'marketing' | 'welcome_in';

export type ClearstreamSyncResult = {
  scope?: ClearstreamSyncScope;
  upserted?: number;
  keywordsUpserted?: number;
  keywordsDeactivated?: number;
  listsUpserted?: number;
  listsDeactivated?: number;
  subscribersUpserted?: number;
  membershipsUpserted?: number;
  membershipsUnsubscribed?: number;
  visitorsAdded?: number;
  visitorHistoryAdded?: number;
  skippedMissingContact?: number;
  skippedCreateUser?: number;
  error?: string;
};

export type ClearstreamSyncMeta = {
  scope: ClearstreamSyncScope;
  last_synced_at: string | null;
  last_result: Record<string, unknown> | null;
};

const extractRequestId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const candidate = record.requestId ?? record.request_id ?? record.errorId ?? record.error_id;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
};

const extractInvokeErrorMessage = async (error: unknown): Promise<string> => {
  if (!error || typeof error !== 'object') return 'Unable to sync from Clearstream.';

  const err = error as {
    message?: string;
    context?: {
      status?: number;
      clone?: () => { json?: () => Promise<unknown>; text?: () => Promise<string> };
      json?: () => Promise<unknown>;
      text?: () => Promise<string>;
    };
  };

  const responseLike = err.context && typeof err.context === 'object' ? err.context : null;
  let payload: unknown = null;
  if (responseLike) {
    const source = typeof responseLike.clone === 'function' ? responseLike.clone() : responseLike;
    if (typeof source.json === 'function') {
      try {
        payload = await source.json();
      } catch {
        payload = null;
      }
    }
    if (!payload && typeof source.text === 'function') {
      try {
        const text = (await source.text()).trim();
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch {
            payload = { error: text };
          }
        }
      } catch {
        payload = null;
      }
    }
  }

  const bodyMessage =
    payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
      ? (payload as { error: string }).error.trim()
      : '';

  return bodyMessage || err.message?.trim() || 'Unable to sync from Clearstream.';
};

export const useClearstreamSyncMeta = (scope: ClearstreamSyncScope) =>
  useQuery<ClearstreamSyncMeta | null>({
    queryKey: ['clearstream-sync-meta', scope],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clearstream_sync_meta')
        .select('scope, last_synced_at, last_result')
        .eq('scope', scope)
        .maybeSingle();
      if (error) throw error;
      return (data as ClearstreamSyncMeta) ?? null;
    },
    staleTime: 60_000,
  });

export const useSyncClearstream = (scope: ClearstreamSyncScope) =>
  useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-clearstream', {
        body: { scope },
      });
      if (error) {
        const message = await extractInvokeErrorMessage(error);
        console.error('[SyncClearstream] invoke error', { scope, message, error });
        throw new Error(message);
      }
      const result = (data ?? {}) as ClearstreamSyncResult;
      if (result.error) {
        console.error('[SyncClearstream] function error payload', {
          scope,
          requestId: extractRequestId(data),
          responseBody: data,
        });
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clearstream-sync-meta', scope] });
      if (scope === 'welcome_in') {
        queryClient.invalidateQueries({ queryKey: ['welcome-in-opt-ins'] });
        queryClient.invalidateQueries({ queryKey: ['welcome-in-opt-in-sync-meta'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['marketing', 'keywords'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'channels'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'subscribers'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'memberships'] });
    },
  });

export const formatClearstreamSyncSummary = (result: ClearstreamSyncResult): string => {
  if (result.scope === 'welcome_in' || result.upserted !== undefined) {
    const lines = [`Synced ${result.upserted ?? 0} Welcome In opt-ins from Clearstream.`];
    if (result.visitorsAdded !== undefined) lines.push(`Added ${result.visitorsAdded} visitors.`);
    if (result.visitorHistoryAdded !== undefined) {
      lines.push(`Added ${result.visitorHistoryAdded} visitor status entries.`);
    }
    if (result.skippedMissingContact) {
      lines.push(`Skipped ${result.skippedMissingContact} rows missing email/phone.`);
    }
    if (result.skippedCreateUser) {
      lines.push(
        `Skipped ${result.skippedCreateUser} rows due to user creation failures. Check function logs.`
      );
    }
    return lines.join('\n');
  }

  return [
    'Synced marketing data from Clearstream.',
    `Keywords: ${result.keywordsUpserted ?? 0} (${result.keywordsDeactivated ?? 0} marked inactive)`,
    `Lists: ${result.listsUpserted ?? 0} (${result.listsDeactivated ?? 0} marked inactive)`,
    `Subscribers: ${result.subscribersUpserted ?? 0}`,
    `List memberships added or restored: ${result.membershipsUpserted ?? 0}`,
    `List memberships ended: ${result.membershipsUnsubscribed ?? 0}`,
  ].join('\n');
};
